import React, { useState, useEffect } from 'react';
import { Button, Tooltip } from '@chakra-ui/react';
import { FiFileText } from 'react-icons/fi';
import { useDispatch, useSelector } from 'react-redux';
import { CustomToast } from '../../../helpers/toast';
import {
    Page,
    Text,
    View,
    Document,
    StyleSheet,
    Image,
    pdf,
} from '@react-pdf/renderer';
import { obtenerConsolidadoDeNotasPorPeriodo } from '../../../features/notaSlice';
import { getAllConfiguraciones } from '../../../features/configuracionSlice';
import LogoColegio from '../../../assets/img/logoColegio.png';
import globalInformation from '../../../helpers/globalInformation';

const styles = StyleSheet.create({
    page: {
        flexDirection: 'column',
        backgroundColor: '#FFFFFF',
        padding: 20,
        fontSize: 8,
        fontFamily: 'Helvetica',
    },

    // Encabezado institucional
    headerContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 6,
        borderBottomWidth: 2,
        borderBottomColor: '#2C3E50',
        paddingBottom: 6,
    },

    logoContainer: {
        width: 45,
        height: 45,
        marginRight: 10,
    },

    schoolInfoContainer: {
        alignItems: 'center',
    },

    schoolName: {
        fontSize: 11,
        fontWeight: 700,
        color: '#2C3E50',
        marginBottom: 1,
        textAlign: 'center',
    },

    schoolDecrees: {
        fontSize: 6,
        color: '#34495E',
        marginBottom: 0.5,
        textAlign: 'center',
    },

    schoolCode: {
        fontSize: 7,
        fontWeight: 600,
        color: '#34495E',
        marginTop: 1,
        textAlign: 'center',
    },

    reportTitle: {
        fontSize: 9,
        fontWeight: 900,
        color: '#0f3460',
        marginTop: 1,
        marginBottom: 1,
        textAlign: 'center',
    },

    // Información del reporte
    reportInfoContainer: {
        flexDirection: 'row',
        gap: 3,
        marginBottom: 5,
        backgroundColor: '#f0f3f7',
        padding: 4,
        borderRadius: 3,
        borderLeftWidth: 4,
        borderLeftColor: '#2fa33a',
    },

    infoBox: {
        flex: 1,
        paddingHorizontal: 6,
        paddingVertical: 2,
        borderRightWidth: 1,
        borderRightColor: '#d0d9e3',
    },

    infoBoxLast: {
        borderRightWidth: 0,
    },

    infoLabel: {
        fontSize: 7,
        fontWeight: 900,
        color: '#0f3460',
        textTransform: 'uppercase',
        marginBottom: 1,
    },

    infoValue: {
        fontSize: 6,
        fontWeight: 600,
        color: '#2fa33a',
    },

    // Tabla principal
    tableContainer: {
        borderWidth: 1.5,
        borderColor: '#0f3460',
        borderRadius: 3,
        overflow: 'hidden',
        marginBottom: 5,
    },

    // Encabezado principal de la tabla
    mainHeaderRow: {
        flexDirection: 'row',
        borderBottomWidth: 2,
        borderBottomColor: '#0f3460',
        backgroundColor: '#0f3460',
        minHeight: 20,
    },

    headerColNum: {
        width: '3%',
        padding: 3,
        borderRightWidth: 1,
        borderRightColor: '#0f3460',
        justifyContent: 'center',
        alignItems: 'center',
    },

    headerColName: {
        width: '20%',
        padding: 3,
        borderRightWidth: 1,
        borderRightColor: '#0f3460',
        justifyContent: 'center',
        alignItems: 'center',
    },

    headerColMateria: {
        flex: 1,
        padding: 2.5,
        borderRightWidth: 1,
        borderRightColor: '#0f3460',
        justifyContent: 'center',
        alignItems: 'center',
    },

    headerColPromedio: {
        width: '6%',
        padding: 3,
        borderRightWidth: 1,
        borderRightColor: '#0f3460',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#2fa33a',
    },

    headerColMerito: {
        width: '4%',
        padding: 3,
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#2fa33a',
    },

    headerMainText: {
        fontSize: 6,
        fontWeight: 900,
        textAlign: 'center',
        color: '#FFFFFF',
    },

    // Filas de datos
    dataRow: {
        flexDirection: 'row',
        borderBottomWidth: 0.8,
        borderBottomColor: '#e8ecf1',
        minHeight: 17,
    },

    dataRowAlt: {
        backgroundColor: '#f8fafb',
    },

    dataRowEven: {
        backgroundColor: '#FFFFFF',
    },

    dataCellNum: {
        width: '3%',
        padding: 2.5,
        borderRightWidth: 0.8,
        borderRightColor: '#e8ecf1',
        justifyContent: 'center',
        alignItems: 'center',
    },

    dataCellName: {
        width: '20%',
        padding: 2.5,
        paddingLeft: 3,
        borderRightWidth: 0.8,
        borderRightColor: '#e8ecf1',
        justifyContent: 'center',
        alignItems: 'flex-start',
    },

    dataCellMateria: {
        flex: 1,
        padding: 2.5,
        borderRightWidth: 0.8,
        borderRightColor: '#e8ecf1',
        justifyContent: 'center',
        alignItems: 'center',
    },

    dataCellPromedio: {
        width: '6%',
        padding: 2.5,
        borderRightWidth: 0.8,
        borderRightColor: '#e8ecf1',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#fff5f7',
    },

    dataCellMerito: {
        width: '4%',
        padding: 2.5,
        justifyContent: 'center',
        alignItems: 'center',
    },

    // Estilos de texto
    dataText: {
        fontSize: 6,
        color: '#2c3e50',
        fontWeight: 500,
    },

    dataTextBold: {
        fontSize: 6,
        fontWeight: 700,
        color: '#0f3460',
    },

    notaCell: {
        fontSize: 6,
        fontWeight: 700,
    },

    nameText: {
        fontSize: 6,
        color: '#0f3460',
        fontWeight: 600,
    },

    meritoText: {
        fontSize: 6,
        fontWeight: 800,
        color: '#2fa33a',
        textAlign: 'center',
    },

    // Leyenda
    legendContainer: {
        marginBottom: 5,
        paddingVertical: 4,
        paddingHorizontal: 8,
        backgroundColor: '#f0f3f7',
        borderRadius: 3,
        borderLeftWidth: 4,
        borderLeftColor: '#2fa33a',
    },

    legendTitle: {
        fontSize: 6,
        fontWeight: 800,
        color: '#0f3460',
        marginBottom: 2,
        textTransform: 'uppercase',
    },

    legendGrid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 2,
    },

    legendItem: {
        flexDirection: 'row',
        width: '19%',
        marginBottom: 1,
        alignItems: 'center',
    },

    legendAbbrev: {
        fontSize: 5,
        fontWeight: 900,
        color: '#FFFFFF',
        backgroundColor: '#0f3460',
        paddingVertical: 2,
        paddingHorizontal: 3,
        marginRight: 2,
        borderRadius: 2,
        minWidth: 15,
        textAlign: 'center',
    },

    legendLabel: {
        fontSize: 5,
        color: '#2c3e50',
        fontWeight: 600,
    },

    // Escala de valoración
    scaleContainer: {
        marginTop: 2,
        marginBottom: 2,
        padding: 4,
        backgroundColor: '#f8f9fa',
        borderRadius: 3,
        borderWidth: 1,
        borderColor: '#e5e7e9',
    },

    scaleTitle: {
        fontSize: 6,
        fontWeight: 800,
        color: '#0f3460',
        marginBottom: 3,
        textAlign: 'center',
    },

    scaleRow: {
        flexDirection: 'row',
        justifyContent: 'space-around',
        gap: 3,
    },

    scaleItem: {
        flex: 1,
        padding: 3,
        borderRadius: 2,
        alignItems: 'center',
    },

    scaleItemText: {
        fontSize: 5.5,
        fontWeight: 700,
        color: 'white',
        textAlign: 'center',
    },

    scaleRange: {
        fontSize: 5,
        fontWeight: 600,
        color: 'white',
        textAlign: 'center',
        marginTop: 1,
    },

    // Pie de página
    footerContainer: {
        marginTop: 5,
        paddingTop: 5,
        borderTopWidth: 1.5,
        borderTopColor: '#e8ecf1',
    },

    footerRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginBottom: 2,
    },

    footerLeft: {
        flex: 1,
    },

    footerRight: {
        textAlign: 'right',
    },

    footerLabel: {
        fontSize: 7.5,
        color: '#555',
        marginBottom: 1,
        fontWeight: 600,
    },

    footerValue: {
        fontSize: 8,
        fontWeight: 800,
        color: '#0f3460',
    },

    footerBottomNote: {
        fontSize: 6.5,
        color: '#888',
        marginTop: 5,
        textAlign: 'center',
        fontStyle: 'italic',
        borderTopWidth: 0.8,
        borderTopColor: '#e8ecf1',
        paddingTopWidth: 3,
    },
});

// Función para obtener el color según la calificación
const getGradeColor = grade => {
    if (grade >= 4.7) return '#27AE60'; // Verde oscuro - Superior
    if (grade >= 4.0) return '#2ECC71'; // Verde - Alto
    if (grade >= 3.0) return '#F39C12'; // Naranja - Básico
    return '#E74C3C'; // Rojo - Bajo
};

// Función para abreviar nombres de materias con detección inteligente
const abreviarMateria = (materia) => {
    if (!materia || typeof materia !== 'string') return '—';

    const lowerMateria = materia.toLowerCase().trim();

    // Mapeo específico para materias completas
    const abreviaturasEspecificas = {
        'matemáticas': 'MAT',
        'matemática': 'MAT',
        'lengua castellana': 'LEN',
        'lenguaje': 'LEN',
        'inglés': 'ING',
        'ingles': 'ING',
        'ciencias naturales y educación ambiental': 'C.NAT',
        'ciencias naturales': 'C.NAT',
        'ciencias sociales, historia y geografía': 'C.SOC',
        'ciencias sociales': 'C.SOC',
        'historia': 'HIST',
        'geografía': 'GEO',
        'biología': 'BIO',
        'química': 'QUI',
        'física': 'FIS',
        'educación física, recreación y deporte': 'ED.FIS',
        'educación física': 'ED.FIS',
        'educación artística y cultural': 'ED.ART',
        'educación artística': 'ED.ART',
        'arte': 'ARTE',
        'música': 'MUS',
        'educación religiosa': 'ED.REL',
        'religión': 'RELIG',
        'educación ética y valores': 'ÉTICA',
        'ética y valores': 'ÉTICA',
        'ética': 'ÉTICA',
        'tecnología e informática': 'TEC.INF',
        'tecnología': 'TEC',
        'informática': 'INF',
        'comportamiento': 'COMP',
        'educación civica': 'CIV',
        'computacion': 'COMP',
        'taller': 'TAL',
    };

    // Buscar coincidencia exacta primero
    if (abreviaturasEspecificas[lowerMateria]) {
        return abreviaturasEspecificas[lowerMateria];
    }

    // Si no hay coincidencia exacta, buscar si contiene palabras clave
    if (lowerMateria.includes('ciencias naturales')) return 'C.NAT';
    if (lowerMateria.includes('ciencias sociales')) return 'C.SOC';
    if (lowerMateria.includes('educación física')) return 'ED.FIS';
    if (lowerMateria.includes('educación artística')) return 'ED.ART';
    if (lowerMateria.includes('educación religiosa')) return 'ED.REL';
    if (lowerMateria.includes('ética')) return 'ÉTICA';
    if (lowerMateria.includes('tecnología') && lowerMateria.includes('informática')) return 'TEC.INF';

    // Si no hay coincidencia, tomar las primeras letras de cada palabra importante
    const palabras = lowerMateria.split(' ').filter(p =>
        p.length > 2 && !['de', 'la', 'el', 'los', 'las', 'del', 'y', 'e'].includes(p)
    );

    if (palabras.length >= 2) {
        return palabras.slice(0, 2).map(p => p.charAt(0).toUpperCase()).join('.');
    }

    // Por defecto, tomar las primeras 3-4 letras
    return materia.substring(0, 4).toUpperCase();
};

const API_URL = process.env.REACT_APP_API_URL;
const BASE_URL = API_URL && API_URL.split('/api')[0];

const ConsolidadoPorPeriodoReporte = ({ data, periodo, anio, grado, sede, tutor, usuario, configuracion }) => {
    // Obtener materias únicas y ordenarlas (filtrar undefined)
    const materias = [...new Set(data.flatMap(item =>
        item.notas.map(nota => nota.materiaNombre).filter(m => m)
    ))].sort();

    const logoToUse = configuracion?.logo ? `${BASE_URL}/uploads/${configuracion.logo}` : LogoColegio;

    // Ordenar estudiantes por apellidos
    const sortedData = [...data].sort((a, b) =>
        `${a.estudiante.apellidos} ${a.estudiante.nombres}`.localeCompare(
            `${b.estudiante.apellidos} ${b.estudiante.nombres}`
        )
    );

    // Calcular promedios basado en el período actual
    const dataConPromedio = sortedData.map(item => {
        const notasValidas = item.notas.filter(n => n.promedio != null && n.promedio > 0);
        const promedioGeneral = notasValidas.length > 0
            ? notasValidas.reduce((sum, n) => sum + n.promedio, 0) / notasValidas.length
            : 0;
        return {
            ...item,
            promedioGeneral
        };
    });

    // Ordenar por promedio para calcular mérito
    const dataOrdenadaPorMerito = [...dataConPromedio].sort((a, b) => b.promedioGeneral - a.promedioGeneral);

    // Calcular promedio del grado
    const promedioDelGrado = dataConPromedio.length > 0
        ? (dataConPromedio.reduce((sum, item) => sum + item.promedioGeneral, 0) / dataConPromedio.length).toFixed(2)
        : 0;

    // Mapeo de período a propiedad de bimestre
    const getPeriodoProp = (p) => {
        const mapping = {
            '1': 'bimestre1',
            '2': 'bimestre2',
            '3': 'bimestre3',
            '4': 'bimestre4',
        };
        return mapping[String(p)] || 'bimestre1';
    };

    const periodoProp = getPeriodoProp(periodo);
    const hoy = new Date();
    const fechaFormato = hoy.toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' });

    return (
        <Document>
            <Page size="A4" orientation="landscape" style={styles.page}>
                {/* Encabezado con información de la institución */}
                <View style={styles.headerContainer}>
                    <View style={styles.logoContainer}>
                        <Image src={logoToUse} />
                    </View>
                    <View style={styles.schoolInfoContainer}>
                        <Text style={styles.schoolName}>
                            {configuracion?.nombreColegio || globalInformation?.colegioNombre || 'INSTITUCION EDUCATIVA EL ASERRÍO'}
                        </Text>
                        {globalInformation?.decretos?.map((decreto, index) => (
                            <Text key={index} style={styles.schoolDecrees}>
                                {decreto?.tipo} N° {decreto.numero} del {decreto.fecha}
                            </Text>
                        ))}
                        <Text style={styles.schoolCode}>SEDE: {sede}</Text>
                    </View>
                </View>

                {/* Título del reporte centrado */}
                <Text style={styles.reportTitle}>CONSOLIDADO DE CALIFICACIONES POR PERÍODO</Text>

                {/* Información del Reporte */}
                <View style={styles.reportInfoContainer}>
                    <View style={styles.infoBox}>
                        <Text style={styles.infoLabel}>Grado</Text>
                        <Text style={styles.infoValue}>{grado}</Text>
                    </View>
                    <View style={styles.infoBox}>
                        <Text style={styles.infoLabel}>Período</Text>
                        <Text style={styles.infoValue}>{anio ? `${anio} - ${periodo}` : periodo}</Text>
                    </View>
                    <View style={styles.infoBox}>
                        <Text style={styles.infoLabel}>Docente Titular</Text>
                        <Text style={styles.infoValue}>{tutor}</Text>
                    </View>
                    <View style={[styles.infoBox, styles.infoBoxLast]}>
                        <Text style={styles.infoLabel}>Total</Text>
                        <Text style={styles.infoValue}>{dataConPromedio.length}</Text>
                    </View>
                </View>

                {/* Leyenda de materias */}
                <View style={styles.legendContainer}>
                    <Text style={styles.legendTitle}>Leyenda de Materias</Text>
                    <View style={styles.legendGrid}>
                        {materias.map((materia, idx) => (
                            <View key={idx} style={styles.legendItem}>
                                <Text style={styles.legendAbbrev}>{abreviarMateria(materia)}</Text>
                                <Text style={styles.legendLabel}>{materia}</Text>
                            </View>
                        ))}
                    </View>
                </View>

                {/* Tabla principal */}
                <View style={styles.tableContainer}>
                    {/* Encabezado principal */}
                    <View style={styles.mainHeaderRow}>
                        <View style={styles.headerColNum}>
                            <Text style={styles.headerMainText}>N°</Text>
                        </View>
                        <View style={styles.headerColName}>
                            <Text style={styles.headerMainText}>ESTUDIANTE</Text>
                        </View>
                        {materias.map((materia, idx) => (
                            <View key={idx} style={styles.headerColMateria}>
                                <Text style={styles.headerMainText}>{abreviarMateria(materia)}</Text>
                            </View>
                        ))}
                        <View style={styles.headerColPromedio}>
                            <Text style={styles.headerMainText}>PROM.</Text>
                        </View>
                        <View style={styles.headerColMerito}>
                            <Text style={styles.headerMainText}>RANK.</Text>
                        </View>
                    </View>

                    {/* Filas de estudiantes */}
                    {dataConPromedio.map((item, index) => {
                        const merito = dataOrdenadaPorMerito.findIndex(d =>
                            d.estudiante._id === item.estudiante._id
                        ) + 1;

                        return (
                            <View
                                key={item.estudiante._id}
                                style={[styles.dataRow, index % 2 === 0 ? styles.dataRowAlt : styles.dataRowEven]}
                                wrap={false}
                            >
                                <View style={styles.dataCellNum}>
                                    <Text style={styles.dataText}>{index + 1}</Text>
                                </View>
                                <View style={styles.dataCellName}>
                                    <Text style={styles.nameText}>
                                        {`${item.estudiante.apellidos}, ${item.estudiante.nombres}`.toUpperCase()}
                                    </Text>
                                </View>
                                {materias.map((materia, idx) => {
                                    const notaMateria = item.notas.find(n => n.materiaNombre === materia);
                                    const notaPeriodo = notaMateria?.[periodoProp]
                                        ? parseFloat(notaMateria[periodoProp])
                                        : null;

                                    const notaColor = notaPeriodo !== null ? getGradeColor(notaPeriodo) : '#95a5a6';

                                    return (
                                        <View key={idx} style={styles.dataCellMateria}>
                                            <Text style={[styles.notaCell, { color: notaColor }]}>
                                                {notaPeriodo !== null ? notaPeriodo.toFixed(1) : '—'}
                                            </Text>
                                        </View>
                                    );
                                })}
                                <View style={styles.dataCellPromedio}>
                                    <Text style={[styles.dataTextBold, {
                                        color: item.promedioGeneral > 0 ? getGradeColor(item.promedioGeneral) : '#0f3460'
                                    }]}>
                                        {item.promedioGeneral > 0 ? item.promedioGeneral.toFixed(2) : '—'}
                                    </Text>
                                </View>
                                <View style={styles.dataCellMerito}>
                                    <Text style={styles.meritoText}>{merito}</Text>
                                </View>
                            </View>
                        );
                    })}
                </View>

                {/* Escala de valoración */}
                <View style={styles.scaleContainer}>
                    <Text style={styles.scaleTitle}>ESCALA DE VALORACIÓN</Text>
                    <View style={styles.scaleRow}>
                        <View style={[styles.scaleItem, { backgroundColor: '#E74C3C' }]}>
                            <Text style={styles.scaleItemText}>BAJO</Text>
                            <Text style={styles.scaleRange}>1.0 - 2.9</Text>
                        </View>
                        <View style={[styles.scaleItem, { backgroundColor: '#F39C12' }]}>
                            <Text style={styles.scaleItemText}>BÁSICO</Text>
                            <Text style={styles.scaleRange}>3.0 - 3.9</Text>
                        </View>
                        <View style={[styles.scaleItem, { backgroundColor: '#2ECC71' }]}>
                            <Text style={styles.scaleItemText}>ALTO</Text>
                            <Text style={styles.scaleRange}>4.0 - 4.6</Text>
                        </View>
                        <View style={[styles.scaleItem, { backgroundColor: '#27AE60' }]}>
                            <Text style={styles.scaleItemText}>SUPERIOR</Text>
                            <Text style={styles.scaleRange}>4.7 - 5.0</Text>
                        </View>
                    </View>
                </View>

                {/* Pie de página */}
                <View style={styles.footerContainer}>
                    <View style={styles.footerRow}>
                        <View style={styles.footerLeft}>
                            <Text style={styles.footerLabel}>Promedio General del Grado</Text>
                            <Text style={styles.footerValue}>{promedioDelGrado}</Text>
                        </View>
                        <View style={styles.footerRight}>
                            <Text style={styles.footerLabel}>Fecha de Generación</Text>
                            <Text style={styles.footerValue}>{fechaFormato}</Text>
                        </View>
                    </View>
                    <Text style={styles.footerBottomNote}>
                        Documento generado automáticamente por el sistema de calificaciones. Vigencia: Período {periodo}
                    </Text>
                </View>
            </Page>
        </Document>
    );
};



const ConsolidadoPorPeriodoReporteButton = ({ datos, dato_grado, dato_periodo }) => {
    const [isGenerating, setIsGenerating] = useState(false);
    const dispatch = useDispatch();
    const { sedeSeleccionada, user } = useSelector(state => state.auth);
    const { configuracion } = useSelector(state => state.configuraciones);

    // Aquí podrías despachar la acción para obtener configuraciones si no se ha hecho antes
    useEffect(() => { dispatch(getAllConfiguraciones()) }, [dispatch]);

    const generateAndDownloadPdf = async () => {
        if (!datos || datos.length === 0) {
            CustomToast({
                title: 'Error',
                message: 'No hay datos para generar el consolidado',
                type: 'error',
                duration: 3000,
                position: 'top',
            });
            return;
        }

        setIsGenerating(true);

        try {
            const getData = await dispatch(obtenerConsolidadoDeNotasPorPeriodo(datos)).unwrap();

            if (!getData || !getData.length) {
                throw new Error(
                    'No se encontraron datos de calificaciones para este grupo'
                );
            }

            const pdfDoc = (
                <ConsolidadoPorPeriodoReporte
                    data={getData}
                    periodo={datos?.periodo}
                    anio={dato_periodo?.year}
                    grado={dato_grado?.nombre || '—'}
                    tutor={dato_grado?.docente_titular?.nombre || '—'}
                    sede={sedeSeleccionada?.nombre || '—'}
                    usuario={user?.usuario?.nombre || '—'}
                    configuracion={configuracion}
                />
            );

            const blob = await pdf(pdfDoc).toBlob();
            const url = URL.createObjectURL(blob);

            const fileName = `consolidado_${dato_grado._id}_${dato_periodo?.year}_p${datos?.periodo}_${new Date().toISOString().slice(0, 10)}.pdf`;

            const link = document.createElement('a');
            link.href = url;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);

            CustomToast({
                title: 'Éxito',
                message: 'Consolidado de notas generado correctamente',
                type: 'success',
                duration: 3000,
                position: 'top',
            });
        } catch (error) {
            console.error('Error al generar PDF:', error);
            CustomToast({
                title: 'Error',
                message: error.message || 'Error al generar el consolidado de notas',
                type: 'error',
                duration: 3000,
                position: 'top',
            });
        } finally {
            setIsGenerating(false);
        }
    };

    return (
        <Tooltip label="Descargar consolidado de calificaciones" placement="auto">
            <Button
                colorScheme="primary"
                size="md"
                mt={6}
                leftIcon={<FiFileText />}
                variant="solid"
                aria-label="Generar consolidado PDF"
                isLoading={isGenerating}
                onClick={generateAndDownloadPdf}
                _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }}
            >
                Generar Consolidado Por Periodo
            </Button>
        </Tooltip>
    );
};

export default ConsolidadoPorPeriodoReporteButton;